import * as THREE from 'three/webgpu';

const DEG2RAD = Math.PI / 180;

export const ATMOSPHERE_QUALITY_PRESETS = Object.freeze( {
	performance: Object.freeze( { label: 'Performance', skyWidth: 96, skyHeight: 48, transmittanceWidth: 128, transmittanceHeight: 32 } ),
	balanced: Object.freeze( { label: 'Balanced', skyWidth: 160, skyHeight: 80, transmittanceWidth: 192, transmittanceHeight: 48 } ),
	quality: Object.freeze( { label: 'Quality', skyWidth: 200, skyHeight: 100, transmittanceWidth: 256, transmittanceHeight: 64 } ),
	cinematic: Object.freeze( { label: 'Cinematic', skyWidth: 256, skyHeight: 128, transmittanceWidth: 256, transmittanceHeight: 64 } )
} );

export const DEFAULT_ATMOSPHERE_PARAMETERS = Object.freeze( {
	quality: 'balanced',
	automaticLighting: true,
	autoExposure: true,
	sunAzimuth: 225,
	sunElevation: 18,
	turbidity: 2.4,
	rayleigh: 1,
	mieCoefficient: 0.005,
	mieDirectionalG: 0.82,
	ozone: 1,
	multipleScattering: 0.28,
	groundAlbedo: '#26383b',
	skyIntensity: 0.68,
	sunIntensity: 1,
	sunsetStartElevation: 18,
	sunsetWarmth: 1,
	sunsetBrightness: 1,
	aerialDensity: 0.00055,
	exposure: 0.52,
	animateTime: false,
	timeScale: 0.55
} );

export const ATMOSPHERE_PARAMETER_RANGES = Object.freeze( {
	sunAzimuth: [ 0, 360 ],
	sunElevation: [ - 18, 90 ],
	turbidity: [ 1, 10 ],
	rayleigh: [ 0.1, 4 ],
	mieCoefficient: [ 0.0001, 0.03 ],
	mieDirectionalG: [ 0.5, 0.95 ],
	ozone: [ 0, 2 ],
	multipleScattering: [ 0, 1.5 ],
	skyIntensity: [ 0.1, 3 ],
	sunIntensity: [ 0, 4 ],
	sunsetStartElevation: [ 2, 30 ],
	sunsetWarmth: [ 0, 2 ],
	sunsetBrightness: [ 0, 4 ],
	aerialDensity: [ 0, 0.004 ],
	exposure: [ 0.25, 3 ],
	timeScale: [ 0, 8 ]
} );

function clamp01( value ) {

	return Math.min( 1, Math.max( 0, value ) );

}

function smoothstep( minimum, maximum, value ) {

	const t = clamp01( ( value - minimum ) / ( maximum - minimum ) );
	return t * t * ( 3 - 2 * t );

}

function airMassForElevation( elevationDegrees ) {

	const clampedElevation = Math.max( 0, elevationDegrees );
	const zenithDegrees = 90 - clampedElevation;
	const cosine = Math.cos( zenithDegrees * DEG2RAD );
	return 1 / Math.max( 0.025, cosine + 0.50572 * Math.pow( 96.07995 - zenithDegrees, - 1.6364 ) );

}

function colorFromLinear( red, green, blue ) {

	return new THREE.Color().setRGB( Math.max( 0, red ), Math.max( 0, green ), Math.max( 0, blue ) );

}

export function clampAtmosphereParameters( parameters ) {

	const next = { ...parameters };

	if ( ! ATMOSPHERE_QUALITY_PRESETS[ next.quality ] ) next.quality = DEFAULT_ATMOSPHERE_PARAMETERS.quality;

	for ( const [ key, range ] of Object.entries( ATMOSPHERE_PARAMETER_RANGES ) ) {

		if ( Number.isFinite( next[ key ] ) ) next[ key ] = Math.min( range[ 1 ], Math.max( range[ 0 ], next[ key ] ) );

	}

	next.automaticLighting = next.automaticLighting !== false;
	next.autoExposure = next.autoExposure !== false;
	next.animateTime = next.animateTime === true;

	try {

		new THREE.Color( next.groundAlbedo );

	} catch {

		next.groundAlbedo = DEFAULT_ATMOSPHERE_PARAMETERS.groundAlbedo;

	}

	return next;

}

export function createAtmosphereParameters( overrides = {} ) {

	return clampAtmosphereParameters( { ...DEFAULT_ATMOSPHERE_PARAMETERS, ...overrides } );

}

/**
 * CPU-side companion to the GPU atmosphere. It derives the handful of
 * low-frequency lighting values needed by Three.js lights and the cloud
 * marcher from the same physical controls used to build the GPU LUTs.
 */
export function evaluateAtmosphereLighting( parameters ) {

	const p = clampAtmosphereParameters( parameters );
	const azimuth = p.sunAzimuth * DEG2RAD;
	const elevation = p.sunElevation * DEG2RAD;
	const horizontal = Math.cos( elevation );
	const sunDirection = new THREE.Vector3(
		Math.sin( azimuth ) * horizontal,
		Math.sin( elevation ),
		Math.cos( azimuth ) * horizontal
	).normalize();

	const airMass = airMassForElevation( p.sunElevation );
	const daylight = smoothstep( - 6, 0.35, p.sunElevation );
	const twilight = smoothstep( - 12, - 1.5, p.sunElevation ) * ( 1 - smoothstep( 1, 12, p.sunElevation ) );
	const aerosol = 0.0038 * p.turbidity + p.mieCoefficient * 0.55;
	const rayleighExtinction = [ 0.0062, 0.0142, 0.0335 ].map( ( value ) => value * p.rayleigh );
	const ozoneExtinction = [ 0.0035, 0.0065, 0.0012 ].map( ( value ) => value * p.ozone );
	const transmittance = rayleighExtinction.map( ( value, index ) => (
		Math.exp( - ( value + ozoneExtinction[ index ] + aerosol ) * airMass ) * daylight
	) );
	const normalization = Math.max( 0.0001, ...transmittance );
	const sunColor = colorFromLinear(
		transmittance[ 0 ] / normalization,
		transmittance[ 1 ] / normalization,
		transmittance[ 2 ] / normalization
	);

	const haze = clamp01( ( p.turbidity - 1 ) / 9 );
	const sunsetProgress = 1 - smoothstep( 0, p.sunsetStartElevation, p.sunElevation );
	const sunsetColorMix = clamp01( sunsetProgress * p.sunsetWarmth * 0.72 );
	const sunsetTarget = colorFromLinear(
		1,
		THREE.MathUtils.lerp( 0.34, 0.17, haze ),
		THREE.MathUtils.lerp( 0.09, 0.025, clamp01( p.ozone * 0.5 ) )
	);
	sunColor.lerp( sunsetTarget, sunsetColorMix );
	const directEnergy = Math.max( ...transmittance ) * p.sunIntensity
		* ( 1 + sunsetProgress * p.sunsetBrightness );
	const highSun = smoothstep( - 2, 30, p.sunElevation );
	const zenithDay = [
		0.075 + haze * 0.12,
		0.24 + haze * 0.1,
		0.62 - haze * 0.2
	];
	const zenithTwilight = [ 0.035 + twilight * 0.055, 0.025 + twilight * 0.02, 0.09 + twilight * 0.08 ];
	const zenithColor = colorFromLinear(
		THREE.MathUtils.lerp( zenithTwilight[ 0 ], zenithDay[ 0 ], highSun ) * p.skyIntensity,
		THREE.MathUtils.lerp( zenithTwilight[ 1 ], zenithDay[ 1 ], highSun ) * p.skyIntensity,
		THREE.MathUtils.lerp( zenithTwilight[ 2 ], zenithDay[ 2 ], highSun ) * p.skyIntensity
	);
	const warmHorizon = [
		0.17 + sunColor.r * 0.34,
		0.12 + sunColor.g * 0.22,
		0.13 + sunColor.b * 0.14
	];
	const daylightHorizon = [ 0.28 + haze * 0.17, 0.38 + haze * 0.08, 0.48 - haze * 0.08 ];
	const horizonSunset = 1 - smoothstep(
		Math.max( - 2, p.sunsetStartElevation - 16 ),
		p.sunsetStartElevation,
		p.sunElevation
	);
	const horizonColor = colorFromLinear(
		THREE.MathUtils.lerp( daylightHorizon[ 0 ], warmHorizon[ 0 ], horizonSunset ) * p.skyIntensity,
		THREE.MathUtils.lerp( daylightHorizon[ 1 ], warmHorizon[ 1 ], horizonSunset ) * p.skyIntensity,
		THREE.MathUtils.lerp( daylightHorizon[ 2 ], warmHorizon[ 2 ], horizonSunset ) * p.skyIntensity
	);
	const groundAlbedo = new THREE.Color( p.groundAlbedo );
	const ambientTop = zenithColor.clone().multiplyScalar( 0.72 + 0.22 * daylight );
	const ambientBottom = groundAlbedo.clone().multiplyScalar( 0.14 + directEnergy * 0.2 )
		.add( horizonColor.clone().multiplyScalar( 0.1 + 0.08 * p.multipleScattering ) );
	const blueHour = smoothstep( - 12, - 4, p.sunElevation ) * ( 1 - smoothstep( - 4, 2, p.sunElevation ) );
	const exposure = p.exposure * ( p.autoExposure
		? THREE.MathUtils.lerp( 1.34, 1, smoothstep( - 4, 18, p.sunElevation ) ) * ( 1 + blueHour * 0.45 )
		: 1 );

	return {
		sunDirection,
		sunColor,
		sunTransmittance: sunColor.clone().multiplyScalar(
			directEnergy / Math.max( p.sunIntensity, 0.0001 )
		),
		directIntensity: directEnergy,
		ambientTop,
		ambientBottom,
		horizonColor,
		zenithColor,
		groundColor: groundAlbedo,
		aerialDensity: p.aerialDensity * THREE.MathUtils.lerp( 1.35, 1, highSun ),
		exposure,
		daylight,
		twilight
	};

}
